<script setup lang="ts">
import {
  NConfigProvider,
  NDialogProvider,
  NMessageProvider,
  dateZhCN,
  zhCN,
  type GlobalThemeOverrides,
} from 'naive-ui'
import AppShell from '@/components/AppShell.vue'
import ErrorBoundary from '@/components/ErrorBoundary.vue'

/**
 * 只放 naive-ui 的三个 Provider —— `useMessage()` / `useDialog()` 必须在 Provider
 * **之内**的组件里才能取到，所以真正的布局在子组件 AppShell 里。
 * ErrorBoundary 包在最外层，捕获整个子树渲染期的未处理异常。
 */
/**
 * Naive UI 会把主题颜色交给 seemly 做 rgba 运算，因此这里不能直接传
 * `var(--token)`。从 :root 读取并展开纯变量别名，既保留 CSS token 为唯一
 * 颜色来源，也确保交给 Naive UI 的是可解析的最终值。
 */
function resolveCssToken(name: `--${string}`): string | undefined {
  const rootStyle = getComputedStyle(document.documentElement)
  const visited = new Set<string>()
  let current = name

  while (!visited.has(current)) {
    visited.add(current)
    const value = rootStyle.getPropertyValue(current).trim()
    if (!value) return undefined

    const alias = /^var\(\s*(--[\w-]+)\s*\)$/.exec(value)
    if (!alias) return value.includes('var(') ? undefined : value
    current = alias[1] as `--${string}`
  }

  return undefined
}

const themeOverrides: GlobalThemeOverrides = {
  common: {
    fontFamily: resolveCssToken('--ui-font-sans'),
    fontSize: '13px',
    fontSizeMedium: '13px',
    heightMedium: '32px',
    textColorBase: resolveCssToken('--ink'),
    textColor1: resolveCssToken('--ink'),
    textColor2: resolveCssToken('--ink-2'),
    textColor3: resolveCssToken('--ink-3'),
    primaryColor: resolveCssToken('--ui-accent'),
    primaryColorHover: resolveCssToken('--accent-strong'),
    primaryColorPressed: resolveCssToken('--brand-600'),
    infoColor: resolveCssToken('--ui-state-refresh-fg'),
    successColor: resolveCssToken('--ui-state-success-fg'),
    warningColor: resolveCssToken('--ui-state-partial-fg'),
    errorColor: resolveCssToken('--ui-state-error-fg'),
    bodyColor: resolveCssToken('--canvas'),
    cardColor: resolveCssToken('--ui-surface'),
    borderColor: resolveCssToken('--ui-border'),
    borderRadius: resolveCssToken('--r-sm'),
    boxShadow1: resolveCssToken('--shadow-1'),
    boxShadow2: resolveCssToken('--shadow-2'),
    boxShadow3: resolveCssToken('--shadow-3'),
  },
  // v5.1 warm desktop modal geometry; keep each component’s real workflow.
  Modal: {
    borderRadius: '12px',
  },
  Drawer: {
    borderRadius: '12px 0 0 12px',
  },
  Dialog: {
    borderRadius: '12px',
  },
}
</script>

<template>
  <error-boundary>
    <n-config-provider :locale="zhCN" :date-locale="dateZhCN" :theme-overrides="themeOverrides">
      <n-message-provider :max="3">
        <n-dialog-provider>
          <app-shell />
        </n-dialog-provider>
      </n-message-provider>
    </n-config-provider>
  </error-boundary>
</template>
